import React, { createContext, useContext, useEffect, useMemo, useState } from 'react';

const STORAGE_KEY = 'crm.theme.preferences';
const STORAGE_VERSION = 2;
const LEGACY_MODE_KEY = 'theme';
const LEGACY_ACCENT_KEY = 'themeAccent';

const HEX = /^#(?:[0-9a-f]{3}|[0-9a-f]{6})$/i;

export const ACCENT_PRESETS = {
  abon: { key: 'abon', label: 'Abon Blue', primary: '#5BB8F5', secondary: '#4A9FE0' },
  emerald: { key: 'emerald', label: 'Emerald', primary: '#22c55e', secondary: '#10b981' },
  violet: { key: 'violet', label: 'Violet', primary: '#7c3aed', secondary: '#a855f7' },
  blue: { key: 'blue', label: 'Blue', primary: '#2563eb', secondary: '#0ea5e9' },
  orange: { key: 'orange', label: 'Orange', primary: '#f97316', secondary: '#f59e0b' },
  teal: { key: 'teal', label: 'Teal', primary: '#0f766e', secondary: '#14b8a6' },
};

function safeParse(value) {
  try {
    return JSON.parse(value);
  } catch {
    return null;
  }
}

function getPreferenceScope() {
  const user = safeParse(localStorage.getItem('user')) || {};
  const tenant = safeParse(localStorage.getItem('activeTenant')) || {};
  const userId = user?.id || user?.user_id || user?.pk || user?.email || 'anonymous';
  const tenantId = tenant?.id || tenant?.tenant_id || tenant?.domain || 'default';
  return `${userId}:${tenantId}`;
}

function getScopedStorageKey(scope) {
  return `${STORAGE_KEY}.${scope}`;
}

function toRgbParts(value = '#5BB8F5') {
  const input = String(value).trim();
  if (!HEX.test(input)) return [91, 184, 245];
  const raw = input.slice(1);
  const full =
    raw.length === 3
      ? raw
          .split('')
          .map((chunk) => `${chunk}${chunk}`)
          .join('')
      : raw;
  const parsed = Number.parseInt(full, 16);
  if (!Number.isFinite(parsed)) return [34, 197, 94];
  return [(parsed >> 16) & 255, (parsed >> 8) & 255, parsed & 255];
}

function toRgbTuple(value = '#5BB8F5') {
  const [red, green, blue] = toRgbParts(value);
  return `${red}, ${green}, ${blue}`;
}

function clamp(value, min, max) {
  return Math.min(max, Math.max(min, value));
}

function rgbToHex([red, green, blue]) {
  const value = [red, green, blue]
    .map((channel) => clamp(Math.round(channel), 0, 255).toString(16).padStart(2, '0'))
    .join('');
  return `#${value}`;
}

function mixHex(a, b, weight = 0.5) {
  const left = toRgbParts(a);
  const right = toRgbParts(b);
  const ratio = clamp(weight, 0, 1);
  return rgbToHex([
    left[0] + (right[0] - left[0]) * ratio,
    left[1] + (right[1] - left[1]) * ratio,
    left[2] + (right[2] - left[2]) * ratio,
  ]);
}

function relativeLuminance(color) {
  const [r, g, b] = toRgbParts(color).map((value) => value / 255);
  const lin = [r, g, b].map((value) =>
    value <= 0.03928 ? value / 12.92 : ((value + 0.055) / 1.055) ** 2.4
  );
  return lin[0] * 0.2126 + lin[1] * 0.7152 + lin[2] * 0.0722;
}

function contrastRatio(a, b) {
  const la = relativeLuminance(a);
  const lb = relativeLuminance(b);
  return (Math.max(la, lb) + 0.05) / (Math.min(la, lb) + 0.05);
}

/* The accent used as TEXT on a neutral surface — the tab label, the link, the
   small stat. This was a fixed 24% darken in light mode and 36% lighten in
   dark, which is not enough for a light accent: the brand blue #5BB8F5 landed
   at #4991c4, only 3.2:1 on the light page, and the Operations sub-tab row
   failed on every tab. Nudge toward the ink until it actually clears 4.5:1
   against the page, so the guarantee holds for any accent anyone picks.
   The accent still carries the meaning; it just has to be readable first. */
function readableAccentText(accent, mode) {
  const ink = mode === 'light' ? '#0f172a' : '#ffffff';
  const surface = mode === 'light' ? '#f5f6f8' : '#0b1220';
  let weight = mode === 'light' ? 0.24 : 0.36;
  let candidate = mixHex(accent, ink, weight);
  while (contrastRatio(candidate, surface) < 4.5 && weight < 0.96) {
    weight += 0.04;
    candidate = mixHex(accent, ink, weight);
  }
  return candidate;
}

function getContrastingInk(color) {
  const [r, g, b] = toRgbParts(color).map((value) => value / 255);
  const normalized = [r, g, b].map((value) =>
    value <= 0.03928 ? value / 12.92 : ((value + 0.055) / 1.055) ** 2.4
  );
  const luminance = normalized[0] * 0.2126 + normalized[1] * 0.7152 + normalized[2] * 0.0722;
  // 0.2245 is the real WCAG boundary: above it, dark ink (#0f172a) clears
  // 4.5:1; below it, white does. The old 0.45 cutoff handed WHITE ink to the
  // brand blue #5BB8F5 (L≈0.43 → white-on-fill 2.2:1, the washed-button bug).
  return luminance > 0.2245 ? '#0f172a' : '#ffffff';
}

function normalizePreference(raw) {
  const m = raw?.mode;
  const mode = m === 'light' ? 'light' : m === 'system' ? 'system' : 'dark';
  const accent = ACCENT_PRESETS[raw?.accent] ? raw.accent : 'abon';
  return { mode, accent };
}

function readSystemMode() {
  try {
    return window.matchMedia && window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
  } catch {
    return 'dark';
  }
}

function readStoredPreference(scope) {
  const scopedRaw = safeParse(localStorage.getItem(getScopedStorageKey(scope)));
  if (scopedRaw && typeof scopedRaw === 'object') {
    return normalizePreference(scopedRaw);
  }

  const sharedRaw = safeParse(localStorage.getItem(STORAGE_KEY));
  if (sharedRaw && typeof sharedRaw === 'object') {
    if (sharedRaw?.version === STORAGE_VERSION && sharedRaw?.byScope?.[scope]) {
      return normalizePreference(sharedRaw.byScope[scope]);
    }
    if (sharedRaw?.mode || sharedRaw?.accent) {
      return normalizePreference(sharedRaw);
    }
  }

  const fallbackMode = localStorage.getItem(LEGACY_MODE_KEY);
  const fallbackAccent = localStorage.getItem(LEGACY_ACCENT_KEY);
  return normalizePreference({
    mode: fallbackMode === 'light' ? 'light' : 'dark',
    accent: fallbackAccent,
  });
}

function writeStoredPreference(scope, preference) {
  const normalized = normalizePreference(preference);
  localStorage.setItem(getScopedStorageKey(scope), JSON.stringify(normalized));

  const sharedRaw = safeParse(localStorage.getItem(STORAGE_KEY));
  const byScope =
    sharedRaw && typeof sharedRaw === 'object' && sharedRaw.byScope && typeof sharedRaw.byScope === 'object'
      ? { ...sharedRaw.byScope }
      : {};
  byScope[scope] = normalized;

  localStorage.setItem(
    STORAGE_KEY,
    JSON.stringify({
      version: STORAGE_VERSION,
      mode: normalized.mode,
      accent: normalized.accent,
      lastScope: scope,
      byScope,
    })
  );
}

function applyTheme({ mode, accent }) {
  const palette = ACCENT_PRESETS[accent] || ACCENT_PRESETS.abon;
  const root = document.documentElement;
  const body = document.body;
  const accentRgb = toRgbTuple(palette.primary);
  const secondaryRgb = toRgbTuple(palette.secondary);
  const dimAlpha = mode === 'light' ? 0.12 : 0.16;
  const softAlpha = mode === 'light' ? 0.1 : 0.18;
  const accentText = readableAccentText(palette.primary, mode);
  const accentOne = mode === 'light' ? mixHex(palette.primary, '#ffffff', 0.16) : mixHex(palette.primary, '#ffffff', 0.26);
  const focusAlpha = mode === 'light' ? 0.24 : 0.34;

  /* Write to BOTH <html> and <body>.
     tokens.css re-declares the accent tokens inside `body[data-theme='light']`,
     and a rule on <body> beats an inline custom property set on <html> for every
     element inside body. So in light mode the chosen accent was shadowed by the
     stylesheet's brand blue while dark mode looked correct — picking orange gave
     you a blue "New" button on a light background. */
  const setVar = (name, value) => {
    root.style.setProperty(name, value);
    body.style.setProperty(name, value);
  };

  body.dataset.theme = mode;
  root.dataset.theme = mode;
  body.classList.toggle('dark-mode', mode === 'dark');
  body.classList.toggle('light-mode', mode === 'light');
  body.classList.toggle('dark', mode === 'dark');
  body.classList.toggle('light', mode === 'light');

  setVar('--accentPrimary', palette.primary);
  setVar('--accent', palette.primary);
  setVar('--accentSecondary', palette.secondary);
  setVar('--accent-primary', palette.primary);
  setVar('--accent-secondary', palette.secondary);
  setVar('--color-accent', palette.primary);
  setVar('--accent-rgb', accentRgb);
  setVar('--accent-secondary-rgb', secondaryRgb);
  setVar('--accent-dim', `rgba(${accentRgb}, ${dimAlpha})`);
  setVar('--accent-soft', `rgba(${accentRgb}, ${softAlpha})`);
  // Design Standard v1: nothing glows and nothing is a gradient. The names stay
  // (old rules still reference them) but resolve flat for every accent preset.
  setVar('--accent-glow', 'transparent');
  setVar('--accent-gradient', palette.primary);
  const accentInk = getContrastingInk(palette.primary);
  setVar('--accent-text', accentText);
  setVar('--accent-ink', accentInk);
  setVar('--accent-0', palette.primary);
  setVar('--accent-1', accentOne);
  setVar('--focus-ring', `0 0 0 2px rgba(${accentRgb}, ${focusAlpha})`);

  /* The --color-* accent family was left at its stylesheet defaults, which are
     brand blue and a fixed dark ink. Every non-blue preset therefore rendered
     blue "accent text", and the two dark presets (violet, teal) put dark ink on
     a dark fill. These are the same three values computed above, so the two
     token families can no longer disagree. */
  setVar('--color-accent-text', accentText);
  setVar('--color-accent-contrast', accentInk);
  setVar('--color-text-on-accent', accentInk);
  setVar('--color-accent-subtle', `rgba(${accentRgb}, ${dimAlpha})`);
  setVar('--color-accent-hover', accentOne);
}

const ThemeContext = createContext(null);

export function ThemeProvider({ children }) {
  const scope = useMemo(() => getPreferenceScope(), []);
  const initial = useMemo(() => readStoredPreference(scope), [scope]);
  // `mode` is the PREFERENCE (light | dark | system); `systemMode` is what the OS
  // reports; `resolvedMode` is what actually gets applied.
  const [mode, setModeState] = useState(initial.mode);
  const [accent, setAccent] = useState(ACCENT_PRESETS[initial.accent] ? initial.accent : 'abon');
  const [systemMode, setSystemMode] = useState(() => readSystemMode());

  useEffect(() => {
    const mq = window.matchMedia && window.matchMedia('(prefers-color-scheme: dark)');
    if (!mq) return undefined;
    const onChange = (event) => setSystemMode(event.matches ? 'dark' : 'light');
    if (mq.addEventListener) mq.addEventListener('change', onChange);
    else mq.addListener(onChange);
    return () => {
      if (mq.removeEventListener) mq.removeEventListener('change', onChange);
      else mq.removeListener(onChange);
    };
  }, []);

  const resolvedMode = mode === 'system' ? systemMode : mode;

  useEffect(() => {
    applyTheme({ mode: resolvedMode, accent });
    writeStoredPreference(scope, { mode, accent });
    localStorage.setItem(LEGACY_MODE_KEY, resolvedMode);
    localStorage.setItem(LEGACY_ACCENT_KEY, accent);
  }, [accent, mode, resolvedMode, scope]);

  const value = useMemo(
    () => ({
      mode,
      resolvedMode,
      accent,
      setMode: (nextMode) =>
        setModeState(['light', 'dark', 'system'].includes(nextMode) ? nextMode : 'dark'),
      toggleMode: () => setModeState(resolvedMode === 'dark' ? 'light' : 'dark'),
      setAccent: (nextAccent) => {
        if (ACCENT_PRESETS[nextAccent]) setAccent(nextAccent);
      },
      presets: Object.values(ACCENT_PRESETS),
    }),
    [mode, resolvedMode, accent]
  );

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
}

export function useTheme() {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useTheme must be used inside ThemeProvider.');
  }
  return context;
}
